import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { amountToInteger } from '@actual-app/core/shared/util';

import { Interruptor } from '#components/mobile/travesia/pasos/comun';
import { Boton } from '#components/mobile/ui/Boton';
import { ImporteMovimiento } from '#components/mobile/ui/ImporteMovimiento';
import { PildoraCategoria } from '#components/mobile/ui/PildoraCategoria';
import { SeparadorFecha } from '#components/mobile/ui/SeparadorFecha';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, num, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useLocale } from '#hooks/useLocale';

import { BocadilloGavi } from './BocadilloGavi';
import { resumen, seTrae } from './importar';
import type { FilaVista } from './importar';

type PasoVistaProps = {
  filas: FilaVista[];
  /** Día del saldo inicial de la cuenta, si lo tiene. */
  fechaSaldoInicial: string | null;
  fuera: Set<string>;
  onAlternar: (id: string) => void;
  nombreCategoria: (id: string) => string | undefined;
  huecoCategoria: (id: string) => number | null | undefined;
  importando: boolean;
  onTraer: () => void;
  /** Primer mes con Rumbo: lo de antes puede entrar como historia. */
  inicioHistoria: string | null;
  conHistoria: boolean;
  onHistoria: (traer: boolean) => void;
  /** «¿Ya lo tenías?»: las dudas de repetidos, si hay. */
  dudas?: ReactNode;
  /** Filas que son el mismo que uno apuntado: entran y sustituyen a ese. */
  mismos?: Set<string>;
  /** Dudas sin contestar: hasta que no se contestan no se trae nada. */
  pendientes?: number;
};

/** Paso 3: qué es nuevo, qué ya estaba y con qué categoría entra. */
export function PasoVista({
  filas,
  fechaSaldoInicial,
  fuera,
  onAlternar,
  nombreCategoria,
  huecoCategoria,
  importando,
  onTraer,
  inicioHistoria,
  conHistoria,
  onHistoria,
  dudas,
  mismos = new Set(),
  pendientes = 0,
}: PasoVistaProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { nuevos, conCategoria, repetidos, anteriores, historia } = resumen(
    filas,
    fuera,
    conHistoria,
  );
  const traeHistoria = conHistoria && historia > 0;
  const fecha = (dia: string) => monthUtils.format(dia, 'd MMM yyyy', locale);
  // Lo de antes del saldo inicial no se lista uno a uno: va resumido.
  const visibles = filas.filter(f => !f.antes);
  const soloAntes = filas.filter(f => f.antes && !f.historia);
  const deHistoria = filas.filter(f => f.historia);
  const mesInicio =
    inicioHistoria &&
    monthUtils.format(inicioHistoria, 'MMMM', locale).toLowerCase();

  const cifras = [
    {
      n: nuevos,
      texto: t('new ones'),
      tinta: color.accent,
      fondo: color.accentSoft,
    },
    historia > 0
      ? {
          n: historia,
          texto: t('of history'),
          tinta: color.fg,
          fondo: color.surface,
        }
      : {
          n: conCategoria,
          texto: t('with category'),
          tinta: color.fg,
          fondo: color.surface,
        },
    {
      n: repetidos,
      texto: t('you already had'),
      tinta: color.fg2,
      fondo: color.surface,
    },
  ];

  return (
    <View
      style={{
        flex: 1,
        gap: 14,
        padding: espacio.margen,
        // Columna con scroll: sin esto, con las dudas encima, los bloques se
        // encogen y se pisan.
        '& > *': { flexShrink: 0 },
      }}
    >
      <View
        style={{ flexDirection: 'row', gap: 8 }}
        data-testid="importar-cifras"
      >
        {cifras.map(c => (
          <View
            key={c.texto}
            style={{
              flex: 1,
              alignItems: 'center',
              padding: 10,
              borderRadius: 14,
              backgroundColor: c.fondo,
            }}
          >
            <Text
              style={{ ...texto.cifra, ...num, fontSize: 22, color: c.tinta }}
            >
              {c.n}
            </Text>
            <Text style={{ fontSize: 12, color: color.fg2 }}>{c.texto}</Text>
          </View>
        ))}
      </View>

      {dudas}

      {historia > 0 && (
        <Tarjeta style={{ gap: 10 }}>
          <Button
            variant="bare"
            onPress={() => onHistoria(!conHistoria)}
            aria-pressed={conHistoria}
            data-testid="importar-historia"
            style={{
              justifyContent: 'space-between',
              gap: 12,
              padding: 0,
              minHeight: 44,
              backgroundColor: 'transparent',
              color: color.fg,
            }}
          >
            <View style={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
              <Text style={{ ...texto.fila, fontWeight: 800 }}>
                <Trans>Bring the history</Trans>
              </Text>
              <Text style={{ fontSize: 13, color: color.fg2 }}>
                {t('{{count}} transactions from before {{month}}', {
                  count: historia,
                  month: mesInicio ?? '',
                })}
              </Text>
            </View>
            <Interruptor encendido={conHistoria} />
          </Button>
          {conHistoria && (
            <View style={{ gap: 6 }}>
              {[
                t(
                  'They show up in Reports, in the Plan as "Before Rumbo" and in what Gavi tells you.',
                ),
                t(
                  "Today's balance doesn't change: I work out backwards what you started with.",
                ),
                t(
                  "Your months with Rumbo and what you have to budget don't move.",
                ),
              ].map(linea => (
                <Text
                  key={linea}
                  style={{ fontSize: 13, color: color.fg2, lineHeight: 1.35 }}
                >
                  <span style={{ color: color.accent, fontWeight: 800 }}>
                    ✓{' '}
                  </span>
                  {linea}
                </Text>
              ))}
            </View>
          )}
        </Tarjeta>
      )}

      {historia === 0 && anteriores > 0 && fechaSaldoInicial && (
        <BocadilloGavi testId="importar-anteriores">
          {t(
            "{{count}} are from {{date}} or earlier, when you set this account's starting balance. They're already counted in it, so I'm leaving them out to keep the balance and past months as they are.",
            {
              count: anteriores,
              date: monthUtils.format(fechaSaldoInicial, 'd MMM yyyy', locale),
            },
          )}
        </BocadilloGavi>
      )}

      <Tarjeta relleno={0} style={{ overflow: 'hidden' }}>
        {visibles.map((f, i) => {
          const nuevoDia = i === 0 || visibles[i - 1].fecha !== f.fecha;
          const dejado = fuera.has(f.id);
          const mismo = mismos.has(f.id) && !dejado;
          const bloqueado = !seTrae(f);
          const categoria = f.categoria
            ? nombreCategoria(f.categoria)
            : undefined;
          return (
            <Fragment key={f.id}>
              {nuevoDia && (
                <SeparadorFecha
                  fecha={f.fecha}
                  style={{ padding: '10px 14px 4px' }}
                />
              )}
              <Button
                variant="bare"
                isDisabled={bloqueado}
                onPress={() => onAlternar(f.id)}
                data-testid="importar-fila"
                data-ayuda-fila={
                  f.repetido ? 'repetido' : bloqueado ? 'antes' : 'nuevo'
                }
                aria-pressed={!bloqueado ? !dejado : undefined}
                style={{
                  minHeight: 56,
                  padding: '8px 14px',
                  borderRadius: 0,
                  justifyContent: 'space-between',
                  gap: 10,
                  opacity: bloqueado || dejado ? 0.55 : 1,
                  cursor: bloqueado ? 'default' : undefined,
                  backgroundColor: 'transparent',
                  color: color.fg,
                }}
              >
                <View
                  style={{
                    flex: 1,
                    minWidth: 0,
                    gap: 3,
                    alignItems: 'flex-start',
                  }}
                >
                  <Text
                    data-ayuda="concepto"
                    style={{
                      ...texto.fila,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textDecoration:
                        bloqueado || dejado ? 'line-through' : undefined,
                    }}
                  >
                    {f.nombre || '—'}
                  </Text>
                  {mismo ? (
                    <Text style={{ fontSize: 12, color: color.fg2 }}>
                      <Trans>Replaces the one you entered</Trans>
                    </Text>
                  ) : f.repetido ? (
                    <Text style={{ fontSize: 12, color: color.fg2 }}>
                      <Trans>You already had it: it won't be repeated</Trans>
                    </Text>
                  ) : f.antes ? (
                    <Text style={{ fontSize: 12, color: color.fg2 }}>
                      <Trans>Already counted in the starting balance</Trans>
                    </Text>
                  ) : dejado ? (
                    <Text style={{ fontSize: 12, color: color.fg2 }}>
                      <Trans>Left out · tap to bring it</Trans>
                    </Text>
                  ) : categoria ? (
                    <span data-ayuda="categoria">
                      <PildoraCategoria
                        nombre={categoria}
                        hueco={f.categoria ? huecoCategoria(f.categoria) : null}
                        tamano="pequena"
                      />
                    </span>
                  ) : (
                    <Text
                      data-ayuda="categoria"
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: color.warn,
                        backgroundColor: color.warnSoft,
                        borderRadius: 10,
                        padding: '2px 8px',
                      }}
                    >
                      <Trans>No category</Trans>
                    </Text>
                  )}
                </View>
                <span data-ayuda="importe">
                  <PrivacyFilter>
                    <ImporteMovimiento
                      valor={amountToInteger(f.importe)}
                      style={bloqueado ? { color: color.fg2 } : undefined}
                    />
                  </PrivacyFilter>
                </span>
              </Button>
            </Fragment>
          );
        })}
        {soloAntes.length > 0 && fechaSaldoInicial && (
          <ResumenFilas
            cabecera={t('Up to {{date}}', { date: fecha(fechaSaldoInicial) })}
            titulo={t('{{count}} transactions', { count: soloAntes.length })}
            texto={t('Already counted in your starting balance of {{date}}', {
              date: fecha(fechaSaldoInicial),
            })}
          />
        )}
        {deHistoria.length > 0 && (
          <ResumenFilas
            cabecera={t('History')}
            titulo={t('{{count}} transactions', { count: deHistoria.length })}
            texto={
              conHistoria
                ? t('They come in as history, outside your months with Rumbo')
                : t('Left out · turn on "Bring the history"')
            }
            apagado={!conHistoria}
          />
        )}
      </Tarjeta>

      {visibles.some(f => seTrae(f)) && (
        <Text
          style={{ ...texto.secundario, color: color.fg2, textAlign: 'center' }}
          data-testid="importar-pista-fuera"
        >
          <Trans>Tap one to leave it out.</Trans>
        </Text>
      )}

      <View
        style={{
          position: 'sticky',
          bottom: 0,
          paddingTop: 8,
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
          backgroundColor: color.bg,
        }}
      >
        <Boton
          bloque
          isDisabled={
            (nuevos === 0 && !traeHistoria) || pendientes > 0 || importando
          }
          onPress={onTraer}
          data-testid="importar-traer"
        >
          {pendientes > 0
            ? t('Answer the questions above first')
            : traeHistoria
              ? nuevos === 0
                ? t('Bring the history')
                : t('Bring {{count}} new ones and the history', {
                    count: nuevos,
                  })
              : nuevos === 0
                ? t('Nothing new to bring')
                : t('Bring {{count}} transactions', { count: nuevos })}
        </Boton>
      </View>
    </View>
  );
}

/** Varias filas que no se listan una a una (historia, saldo inicial). */
function ResumenFilas({
  cabecera,
  titulo,
  texto: detalle,
  apagado = false,
}: {
  cabecera: string;
  titulo: string;
  texto: string;
  apagado?: boolean;
}) {
  return (
    <View data-testid="importar-resumen-filas">
      <Text
        style={{
          padding: '10px 14px 4px',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: color.fg2,
        }}
      >
        {cabecera}
      </Text>
      <View
        style={{
          minHeight: 56,
          padding: '8px 14px',
          justifyContent: 'center',
          gap: 3,
          opacity: apagado ? 0.55 : 1,
        }}
      >
        <Text style={texto.fila}>{titulo}</Text>
        <Text style={{ fontSize: 12, color: color.fg2 }}>{detalle}</Text>
      </View>
    </View>
  );
}
